import { DatePipe, DecimalPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LucideAngularModule } from 'lucide-angular';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { TableModule } from 'primeng/table';
import { GlobalOverlayService } from '../../../core/ui/global-overlay.service';
import { StatusBadge } from '../../../shared/components/status-badge.component';
import { RefreshButtonComponent } from '../../../shared/components/refresh-button.component';
import {
  CommissionReviewItem,
  CommissionReviewService,
} from '../services/commission-review.service';

type Row = Omit<
  CommissionReviewItem,
  | 'bookingAmount'
  | 'saleCommission'
  | 'specialCommission'
  | 'commissionRate'
  | 'commissionAmount'
> & {
  bookingAmount: number;
  saleCommission: number;
  specialCommission: number;
  commissionRate: number | null;
  recoveryDeduction: number;
  netPaymentAmount: number;
  commissionAmount: number;
  transactionDate: Date | null;
  transactionTime: Date | null;
};

@Component({
  selector: 'app-commission-review-page',
  imports: [DatePipe, DecimalPipe, FormsModule, LucideAngularModule, StatusBadge, RefreshButtonComponent, TableModule, InputTextModule, DatePickerModule],
  templateUrl: './commission-review-page.component.html',
})
export class CommissionReviewPageComponent implements OnInit {
  private readonly overlay = inject(GlobalOverlayService);
  private readonly service = inject(CommissionReviewService);
  readonly rows = signal<Row[]>([]);
  readonly isLoadingCommissions = signal(false);
  readonly selected = signal<Row | null>(null);
  readonly receiptUrlError = signal('');
  readonly transactionDateTimeError = signal('');

  ngOnInit(): void {
    void this.loadCommissions();
  }
  async loadCommissions(): Promise<void> {
    if (this.isLoadingCommissions()) return;
    this.isLoadingCommissions.set(true);
    this.overlay.showLoading('กำลังโหลดรายการค่าคอมมิชชั่นรอตรวจสอบ...');
    try {
      const items = await this.service.getPending();
      this.rows.set(
        items.map((item) => ({
          ...item,
          bookingAmount: Number(item.bookingAmount),
          commissionAmount: Number(item.commissionAmount),
          saleCommission: Number(item.saleCommission),
          specialCommission: Number(item.specialCommission),
          commissionRate: item.commissionRate === null ? null : Number(item.commissionRate),
          recoveryDeduction: Number(item.recoveryDeduction),
          netPaymentAmount: Number(item.netPaymentAmount),
          transactionDate: null,
          transactionTime: null,
        })),
      );
    } catch (error: unknown) {
      this.showError('โหลดค่าคอมมิชชั่นไม่สำเร็จ', error);
    } finally {
      this.isLoadingCommissions.set(false);
      this.overlay.hideLoading();
    }
  }

  open(row: Row): void {
    this.receiptUrlError.set('');
    this.transactionDateTimeError.set('');
    if (!row.transactionDate || !row.transactionTime) {
      const now = new Date();
      row.transactionDate = new Date(now);
      row.transactionTime = new Date(now);
    }
    this.selected.set(row);
  }
  close(): void {
    this.selected.set(null);
  }
  openAttachment(url: string | null): void {
    if (url) window.open(this.service.resolveFileUrl(url), '_blank', 'noopener,noreferrer');
  }
  async approve(row: Row): Promise<void> {
    const receiptUrlError = row.netPaymentAmount > 0 ? this.validateReceiptUrl(row.receiptUrl) : '';
    this.receiptUrlError.set(receiptUrlError);
    const transactionDate = row.transactionDate;
    const transactionTime = row.transactionTime;
    if (!transactionDate || !transactionTime) {
      this.transactionDateTimeError.set('กรุณาระบุวันและเวลาทำรายการ');
      return;
    }
    this.transactionDateTimeError.set('');
    if (receiptUrlError) return;
    const ok = await this.overlay.confirm({
      title: 'อนุมัติค่าคอมมิชชั่น',
      message: row.recoveryDeduction > 0
        ? `ยอดคอม ${row.commissionAmount.toLocaleString()} บาท หักยอดค้าง ${row.recoveryDeduction.toLocaleString()} บาท และโอนสุทธิ ${row.netPaymentAmount.toLocaleString()} บาท`
        : `ยืนยันอนุมัติ ${row.code} จำนวน ${row.netPaymentAmount.toLocaleString()} บาท`,
      confirmText: 'อนุมัติ',
    });
    if (!ok) return;
    this.overlay.showLoading('กำลังอนุมัติค่าคอมมิชชั่น...');
    try {
      await this.service.approve(
        row.id,
        row.receiptUrl.trim(),
        this.toDateValue(transactionDate),
        this.toTimeValue(transactionTime),
        row.sourceType,
      );
      this.remove(row);
      this.overlay.success('อนุมัติสำเร็จ', `${row.code} ผ่านการตรวจสอบจากบัญชีแล้ว`);
    } catch (error: unknown) {
      this.showError('อนุมัติไม่สำเร็จ', error);
    } finally {
      this.overlay.hideLoading();
    }
  }
  clearReceiptUrlError(): void { this.receiptUrlError.set(''); }
  clearTransactionDateTimeError(): void { this.transactionDateTimeError.set(''); }
  private toDateValue(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
  private toTimeValue(value: Date): string {
    return `${String(value.getHours()).padStart(2, '0')}:${String(value.getMinutes()).padStart(2, '0')}`;
  }
  private validateReceiptUrl(value: string): string {
    if (!value.trim()) return 'กรุณากรอกลิงก์ใบเสร็จก่อนอนุมัติ';
    try {
      const url = new URL(value.trim());
      return url.protocol === 'http:' || url.protocol === 'https:' ? '' : 'ลิงก์ใบเสร็จต้องขึ้นต้นด้วย http:// หรือ https://';
    } catch { return 'รูปแบบลิงก์ใบเสร็จไม่ถูกต้อง'; }
  }
  async reject(row: Row): Promise<void> {
    const ok = await this.overlay.confirm({
      title: 'ไม่อนุมัติค่าคอมมิชชั่น',
      message: `ยืนยันไม่อนุมัติ ${row.code} หรือไม่`,
      confirmText: 'ไม่อนุมัติ',
    });
    if (!ok) return;
    this.overlay.showLoading('กำลังบันทึกผลการตรวจสอบ...');
    try {
      await this.service.reject(row.id, row.sourceType);
      this.remove(row);
      this.overlay.success('บันทึกผลแล้ว', `${row.code} ไม่ผ่านการตรวจสอบจากบัญชี`);
    } catch (error: unknown) {
      this.showError('บันทึกผลไม่สำเร็จ', error);
    } finally {
      this.overlay.hideLoading();
    }
  }
  private remove(row: Row): void {
    this.rows.update((rows) =>
      rows.filter((item) => item.id !== row.id || item.sourceType !== row.sourceType),
    );
    this.close();
  }
  private showError(title: string, error: unknown): void {
    const message =
      error instanceof HttpErrorResponse && typeof error.error?.message === 'string'
        ? error.error.message
        : 'กรุณาลองใหม่อีกครั้ง';
    this.overlay.error(title, message);
  }
}
